import { AfterViewInit, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { fadeIn } from 'src/app/animations';
import { Account } from 'src/app/models/auth/account.model';
import { MyWorkService } from 'src/app/services/sections/job/my-work.service';
import { GlobalSettingsService } from 'src/app/services/ui/global-settings.service';
import { sortBy } from 'lodash-es';
import { NavigationActionButton } from '../../navigation-bar/models/navigation-action-button.model';
import { SiteNotesService } from 'src/app/services/sections/site-notes/site-notes.service';
import { DeficienciesService } from 'src/app/services/sections/deficiencies/deficiencies.service';
import { LoginRedirectPage } from '../models/login-redirect-page.enum';
import { AuthService } from 'src/app/services/auth.service';
import { JwtHelperService } from '@auth0/angular-jwt';
import { UserService } from 'src/app/services/sections/shared/user.service';

@Component({
	selector: 'app-accounts-list',
	templateUrl: './accounts-list.component.html',
	styleUrls: ['./accounts-list.component.scss', '../../stylesheets/job-main.scss', '../../stylesheets/job-inputs.scss'],
	animations: [fadeIn(200)]
})
export class AccountsListComponent implements OnInit, AfterViewInit, OnDestroy {

	public userAccounts: Account[] = [];
	public visibleUserAccounts = [];

	public searchFormGroup: FormGroup;

	public redirectTo: LoginRedirectPage = LoginRedirectPage.MyWork;
	public isAutoredirect = false;
	public preferredAccountId = null;

	constructor(
		private activatedRoute: ActivatedRoute,
		private myWorkService: MyWorkService,
		private siteNotesService: SiteNotesService,
		private deficienciesService: DeficienciesService,
		private authService: AuthService,
		private jwt: JwtHelperService,
		private userService: UserService
	) { }

	public ngOnInit(): void {
		GlobalSettingsService.priorityNavigationBarActions = new Map<string, NavigationActionButton>();

		this.redirectTo = parseInt(this.activatedRoute.snapshot.queryParamMap.get('page') || LoginRedirectPage.MyWork.toString(), 10);
		this.userAccounts = this.activatedRoute.snapshot.data.userAccounts || [];
		this.isAutoredirect = !!this.activatedRoute.snapshot.queryParamMap.get('isAutoredirect');

		if (!!this.activatedRoute.snapshot.queryParamMap.get('accountId')) {
			const paramAccountId = parseInt(this.activatedRoute.snapshot.queryParamMap.get('accountId'), 10);

			if (!isNaN(paramAccountId)) {
				this.preferredAccountId = paramAccountId;
			}
		}

		if (this.userAccounts?.length === 1 && (!this.preferredAccountId || (this.userAccounts[0].accountId === this.preferredAccountId))) {
			this.tryRedirect(this.userAccounts[0]);
		} else {
			this.tryAutoredirect();
		}

		this.userAccounts = sortBy(this.userAccounts, [
			(item) => item.accountName.toLowerCase()
		]);

		this.visibleUserAccounts = [...this.userAccounts];

		this.searchFormGroup = new FormGroup({
			query: new FormControl(null)
		});

		this.searchFormGroup.get('query').valueChanges
			.subscribe(query => {
				query = query.toLowerCase();
				this.visibleUserAccounts = this.userAccounts.filter(item => item.accountName.toLowerCase().includes(query));
			});

		GlobalSettingsService.isGlobalLoaderLoading = false;

		if (!this.isAutoredirect) {
			GlobalSettingsService.isSecondaryLoaderLoading = false;
		}

	}

	public ngAfterViewInit(): void {
		setTimeout(() => {
			GlobalSettingsService.isGlobalLoaderLoading = false;

			if (!this.isAutoredirect) {
				GlobalSettingsService.isSecondaryLoaderLoading = false;
			}
		}, 1000);
	}

	public ngOnDestroy(): void {
		GlobalSettingsService.priorityNavigationBarActions = null;
	}

	public tryRedirect(account: Account): void {
		const token = this.jwt.decodeToken();
		const platformTypeId = !!token ? parseInt(token[AuthService.PlatformTypeIdClaim], 10) : null;

		if (!token || !platformTypeId || isNaN(platformTypeId)) {
			this.isAutoredirect = false;
			GlobalSettingsService.isSecondaryLoaderLoading = false;
			this.authService.logout();
		} else {

			this.authService.isUserAuthenticated(token[AuthService.EmailClaim])
				.subscribe(isAuthenticated => {
					if (!!isAuthenticated) {
						if (token[AuthService.AccountIdClaim] === account.accountId.toString()) {
							this.redirectToPage(account.userAccountId);
							localStorage.removeItem('platform_type_id');
						} else {
							this.authService.regenerateToken(platformTypeId, account.accountId)
								.subscribe(isTokenGenerated => {
									localStorage.removeItem('platform_type_id');
									if (!!isTokenGenerated) {
										this.redirectToPage(account.userAccountId);
									} else {
										this.isAutoredirect = false;
										GlobalSettingsService.isSecondaryLoaderLoading = false;
										this.authService.logout();
									}
								});
						}
					}
				}, () => {
					GlobalSettingsService.isSecondaryLoaderLoading = false;
				});
		}
	}

	private redirectToPage(userAccountId: number): void {
		switch (this.redirectTo) {
			case LoginRedirectPage.MyWork: this.redirectToMyWork(userAccountId); break;
			case LoginRedirectPage.SiteNotes: this.redirectToSiteNotes(userAccountId); break;
			case LoginRedirectPage.Deficiencies: this.redirectToDeficiencies(userAccountId); break;
			case LoginRedirectPage.DeleteAccount: this.redirectToDeleteAccount(); break;
			default: this.redirectToMyWork(userAccountId); break;
		}
	}

	private tryAutoredirect(): void {

		if (!!this.preferredAccountId) {
			const preferredAccount = this.userAccounts.find(item => item.accountId === this.preferredAccountId);

			if (!!preferredAccount) {
				this.tryRedirect(preferredAccount);
			}

		} else if (this.isAutoredirect) {
			const token = this.jwt.decodeToken();

			if (!!token[AuthService.PlatformTypeIdClaim] && !!token[AuthService.AccountIdClaim] && !!token[AuthService.UserAccountIdClaim]) {
				const userAccountId = parseInt(token[AuthService.UserAccountIdClaim], 10);
				const accountId = parseInt(token[AuthService.AccountIdClaim], 10);

				const account = this.userAccounts.find(item => item.userAccountId === userAccountId && item.accountId === accountId);

				if (!!account) {
					this.tryRedirect(account);
				} else {
					this.isAutoredirect = false;
					GlobalSettingsService.isSecondaryLoaderLoading = false;
				}
			} else {
				this.isAutoredirect = false;
				GlobalSettingsService.isSecondaryLoaderLoading = false;
			}
		}
	}

	private redirectToMyWork(userAccountId: number): void {
		GlobalSettingsService.isSecondaryLoaderLoading = true;

		this.myWorkService.getMyWorkRedirectUriById(userAccountId)
			.subscribe(({ smartSyncUri }) => {
				location.href = `${location.origin}/${smartSyncUri}`;
			});
	}

	private redirectToSiteNotes(userAccountId: number): void {
		GlobalSettingsService.isSecondaryLoaderLoading = true;

		this.siteNotesService.getSiteNotesRedirectUriById(userAccountId)
			.subscribe(({ smartSyncUri }) => {
				location.href = `${location.origin}/${smartSyncUri}`;
			});
	}

	private redirectToDeficiencies(userAccountId: number): void {
		GlobalSettingsService.isSecondaryLoaderLoading = true;

		this.deficienciesService.getDeficienciesRedirectUriById(userAccountId)
			.subscribe(({ smartSyncUri }) => {
				location.href = `${location.origin}/${smartSyncUri}`;
			});
	}

	private redirectToDeleteAccount(): void {
		GlobalSettingsService.isSecondaryLoaderLoading = true;

		this.userService.getDeleteUserAccountRedirectUri()
			.subscribe(({ smartSyncUri }) => {
				location.href = `${location.origin}/${smartSyncUri}`;
			});
	}
}
